import React from 'react';
import Avatar from '../common/Avatar';
import { Bird } from '../../api/birds';

interface BirdAvatarProps {
  bird?: Bird;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  shape?: 'circle' | 'square' | 'rounded';
  className?: string;
  showBandId?: boolean;
  bandIdPosition?: 'below' | 'overlay';
}

export default function BirdAvatar({
  bird,
  size = 'md',
  shape = 'circle',
  className = '',
  showBandId = false,
  bandIdPosition = 'below',
}: BirdAvatarProps) {
  // Find avatar photo from bird's photos
  const avatarPhoto = React.useMemo(() => {
    if (!bird) return null;
    
    if (bird.avatarPhotoId && bird.photos) {
      const avatar = bird.photos.find(photo => photo.id === bird.avatarPhotoId);
      if (avatar) return avatar;
    }
    
    // Fallback to first photo if no specific avatar is set
    if (bird.photos && bird.photos.length > 0) {
      return bird.photos[0];
    }
    
    return null;
  }, [bird]);

  const fallbackText = bird?.bandId || 'Unknown';
  const alt = bird ? `${bird.bandId} (${bird.species?.commonName || 'Unknown species'})` : 'Unknown bird';

  // Get appropriate border radius for overlay based on shape
  const overlayBottomRadius = React.useMemo(() => {
    switch (shape) {
      case 'circle':
        return 'rounded-b-full';
      case 'rounded':
        return 'rounded-b-lg';
      case 'square':
        return 'rounded-b-none';
      default:
        return 'rounded-b-full';
    }
  }, [shape]);

  if (bandIdPosition === 'overlay') {
    return (
      <div className={`relative ${className}`}>
        <Avatar
          photo={avatarPhoto}
          size={size}
          shape={shape}
          fallbackText={fallbackText}
          alt={alt}
        />
        {showBandId && bird && (
          <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent text-white text-xs font-medium text-center py-1 ${overlayBottomRadius}`}>
            {bird.bandId}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <Avatar
        photo={avatarPhoto}
        size={size}
        shape={shape}
        fallbackText={fallbackText}
        alt={alt}
      />
      {showBandId && bird && (
        <div className="mt-1 text-xs font-medium text-slate-600 text-center">
          {bird.bandId}
        </div>
      )}
    </div>
  );
} 